import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

interface TokenPayload {
  CompanyID: string;
  UserID: string;
  ModulesID: string;
  ClientTypeID: string;
  exp: number;
}

@Injectable({ providedIn: 'root' })
export class AuthService {

  static readonly TOKEN_KEY          = 'token'; 
  static readonly RESTRICTION_KEY    = 'restrictionKey';
  static readonly COMPANY_ID_KEY     = 'companyID';
  static readonly USER_ID_KEY        = 'userId';
  static readonly REDIRECT_KEY       = 'redirect_url';
  static readonly LEVEL_KEY          = 'level';

  constructor(private router: Router) {}

  getToken(): string | null {
  return sessionStorage.getItem(AuthService.TOKEN_KEY);
}

setToken(token: string): void {
  sessionStorage.setItem(AuthService.TOKEN_KEY, token);
}

removeToken(): void {
  sessionStorage.removeItem(AuthService.TOKEN_KEY);
}


  private decodeToken(token: string): TokenPayload | null {
    try {
      const payload = token.split('.')[1];
      const base64  = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json    = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(json) as TokenPayload;
    } catch {
      return null;
    }
  }


  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const payload = this.decodeToken(token);
    if (!payload) {
      this.removeToken();
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      this.logout(false);
      return false;
    }

    return true;
  }


  saveLoginData(token: string, restrictionKey: string): void {
  this.setToken(token); 

  localStorage.setItem(AuthService.RESTRICTION_KEY, restrictionKey ?? '');

  const payload = this.decodeToken(token);
  if (payload) {
    localStorage.setItem(AuthService.COMPANY_ID_KEY, payload.CompanyID);
    localStorage.setItem(AuthService.USER_ID_KEY, payload.UserID);
  }
}


  getUserClaims(): TokenPayload | null {
    const token = this.getToken();
    if (!token) return null;
    return this.decodeToken(token);
  }

  get userID(): string {
    return this.getUserClaims()?.UserID ?? '';
  }

  get companyID(): string {
    return localStorage.getItem(AuthService.COMPANY_ID_KEY) ?? this.getUserClaims()?.CompanyID ?? '';
  }

  get restrictionKey(): string {
    return localStorage.getItem(AuthService.RESTRICTION_KEY) ?? '';
  }

  get level(): string | null {
    return localStorage.getItem(AuthService.LEVEL_KEY);
  }

  get modulesID(): string {
    return this.getUserClaims()?.ModulesID ?? '';
  }

  saveRedirectUrl(url: string): void {
    localStorage.setItem(AuthService.REDIRECT_KEY, url);
  }

  getAndClearRedirectUrl(): string {
    const url = localStorage.getItem(AuthService.REDIRECT_KEY) || '/';
    localStorage.removeItem(AuthService.REDIRECT_KEY);
    localStorage.removeItem(AuthService.LEVEL_KEY);
    return url;
  }

  logout(redirect = true): void {
    sessionStorage.removeItem(AuthService.TOKEN_KEY);
    localStorage.removeItem(AuthService.RESTRICTION_KEY);
    localStorage.removeItem(AuthService.COMPANY_ID_KEY);
    localStorage.removeItem(AuthService.USER_ID_KEY);
    localStorage.removeItem(AuthService.REDIRECT_KEY);
    localStorage.removeItem(AuthService.LEVEL_KEY);

    if (redirect) {
      this.router.navigate(['/login']);
    }
  }
}